import { cord, cord3, formatInput, gql2 } from "@/gql/lib";
import { Subject, InvEntityType } from "../data/model";
import { fixTier3XY, correctTier12XY } from "@/gql/cords";
import { POPULATION_TYPE, PopulationTypeHelper } from "@/common/models";
import { SharedState } from "@/common/state";
import { UseModal } from "@/components/root/modal";
import EventsModalVue from "@/components/modals/events-modal/EventsModal.vue";
import {
  AxisType,
  ActionEnum,
  TimeAxisRangeInput,
  CoordinateInputTier3,
} from "@/gql/types";
import { EventsModalProps } from "@/components/modals/events-modal/model";
import { TimeFrameOption } from "@/common/state/models";

const summary_sentence = (subject: Subject) => {
  switch (subject.type) {
    case POPULATION_TYPE.EMPLOYEE:
      return `Events of ${subject.name}`;
    case POPULATION_TYPE.ORGANIZATION:
      return `Events where ${subject.name} received information`;
    case POPULATION_TYPE.PRIVATE_EMAIL:
      return `Events where ${subject.name} received information`;
    case POPULATION_TYPE.CONTACT:
      return `Events where ${subject.name} received information`;
    case POPULATION_TYPE.FOLDER:
      return `Events involving files contained in ${subject.name} folder`;
    case POPULATION_TYPE.LABEL:
      return `Events that involve ${subject.name}`;
    default:
      return "?";
  }
};

const get_summary = gql2<
  { subject: Subject; relative_time_frame: TimeFrameOption },
  { count: number; relative_count: number; type: InvEntityType; desc: string }
>("inv-counters.events", ({ subject, relative_time_frame }, { frame }) => {
  const cords = (time_frame: TimeAxisRangeInput) =>
    correctTier12XY({
      coordinate: {
        x: cord(AxisType.None),
        y: cord(PopulationTypeHelper.asAxisType2(subject.type, "both"), [
          subject.id,
        ]),
        z: time_frame,
      },
      action: ActionEnum.List,
    });
  return {
    query: `{
     current: tier1count(params:${formatInput(cords(frame))}){
        count
        uniqs
     }
     relative: tier1count(params:${formatInput(
       cords({
         type: relative_time_frame.type,
         values: [relative_time_frame.from],
       })
     )}){
      count
      uniqs
   }
    }`,
    mapper: (res) => {
      const { current, relative } = res;
      return {
        type: POPULATION_TYPE.EVENT,
        count: current.length > 0 ? current[0].count : 0,
        relative_count: relative.length > 0 ? relative[0].count : 0,
        desc: summary_sentence(subject),
      };
    },
  };
});

const get_titles = (subject: Subject) => {
  switch (subject.type) {
    case POPULATION_TYPE.EMPLOYEE:
      return [
        `Events of <b>${subject.name}</b>`,
        `Files accessible by <b>${subject.name}</b>`,
      ];
    case POPULATION_TYPE.LABEL:
      const label_name = subject.name.replace(/information/i, "");
      return [
        `Events that involved <b>${label_name}</b> information`,
        `<b>${label_name}</b> information Files`,
      ];
    case POPULATION_TYPE.ORGANIZATION:
    case POPULATION_TYPE.PRIVATE_EMAIL:
    case POPULATION_TYPE.CONTACT:
      return [
        `Events where <b>${subject.name}</b> received information`,
        `Files that were sent to <b>${subject.name}</b>`,
      ];
    case POPULATION_TYPE.FOLDER:
    default:
      return [
        `Events involving files contained in <b>${subject.name}</b> folder`,
        `Files contained in <b>${subject.name}</b>`,
      ];
  }
};

const get_main_cords = (subject: Subject) => {
  const { from, type } = SharedState.selectedTimeFrameOption.value;
  const cords = fixTier3XY({
    x: cord3(AxisType.None),
    y: cord3(PopulationTypeHelper.asAxisType2(subject.type, "both"), [
      subject.id,
    ]),
    z: {
      type,
      values: [from],
    },
  });
  return cords;
};

const show_modal = async (
  subject: Subject,
  alt_props: {
    coordinates?: CoordinateInputTier3[];
    events_alt_title?: string;
    files_alt_title?: string;
  } = {}
) => {
  const [events_title, files_title] = get_titles(subject);
  const {
    coordinates = [],
    events_alt_title = "",
    files_alt_title = "",
  } = alt_props;
  const props: EventsModalProps = {
    coordinates:
      coordinates.length > 0 ? coordinates : [get_main_cords(subject)],
    files_title: files_alt_title ? files_alt_title : files_title,
    title: events_alt_title ? events_alt_title : events_title,
    hide_filters: [],
    events_only: true,
    show_back: false,
  };
  UseModal().show(EventsModalVue, props);
};

export default { get_summary, show_modal };
